/**
 * @file placement.ts
 * @description The placement kernel: one pure resolver that turns a placement
 * INTENT (where the producer wants content, expressed as an anchor) plus the
 * scene's occupancy into concrete coordinates. Every producer — paste,
 * generation outputs, toolbar creates — expresses intent; nobody hardcodes a
 * point. Search is small local nudges in reading order: content lands NEAR
 * where the user expects, never ON something, and never teleports far away —
 * beyond a bounded search the original anchor wins (predictability over
 * perfection).
 */

import { AUDIO_NODE_WIDTH, MAX_NODE_HEIGHT, NODE_GAP_X, NODE_GAP_Y, NODE_WIDTH } from './canvasConstants'
import { DEFAULT_START_X, DEFAULT_START_Y } from './canvasPanelConstants'
import type { CanvasPosition } from '@/store/yjsStore'

export interface PlacementRect {
  x: number
  y: number
  width: number
  height: number
}

export interface PlacementItem {
  width: number
  height: number
}

export type PlacementIntent =
  /** User aimed (pointer drop / explicit coordinate): exact, never adjusted. */
  | { kind: 'at'; point: { x: number; y: number } }
  /**
   * The spatial grammar: new content joins a flow next to what made it.
   * `right` = alternatives (takes) on the source's row; `below` = transforms.
   */
  | { kind: 'flow'; source: PlacementRect; relation: 'right' | 'below' }
  /** Collision-resolved placement centered on a point (viewport center). */
  | { kind: 'near'; anchor: { x: number; y: number } }
  /** Below the scene's existing content (viewport unknown). */
  | { kind: 'frontier'; contentBounds: PlacementRect | null }

/** Occupancy adapter: returns rects intersecting the probe rect. */
export interface PlacementOccupancy {
  query(rect: PlacementRect): PlacementRect[]
}

export interface PlacementOptions {
  gapX?: number
  gapY?: number
  /** Cluster-internal arrangement for multi-item placements. */
  layout?: 'row' | 'grid'
  /** How far right of the anchor to search before wrapping to the next row. */
  maxSearchSpan?: number
  /** Rows to try before accepting the anchor as-is (predictability wins). */
  maxRows?: number
  /** Overlap (px) tolerated on each side before a rect counts as a blocker. */
  overlapTolerance?: number
}

export interface PlacementResult {
  positions: Array<{ x: number; y: number }>
  cluster: PlacementRect
  /** False when the bounded search gave up and accepted the raw anchor. */
  clean: boolean
}

const DEFAULTS = {
  gapX: NODE_GAP_X,
  gapY: NODE_GAP_Y,
  layout: 'row' as const,
  maxSearchSpan: 2400,
  maxRows: 6,
  overlapTolerance: 2,
}

/**
 * Lay N items out as one cluster (row, or ~square grid of uniform cells) and
 * return their offsets relative to the cluster's top-left plus its size.
 * Batches always move as a unit: one shape, one collision query.
 */
export function layoutCluster(
  items: PlacementItem[],
  layout: 'row' | 'grid',
  gapX: number,
  gapY: number,
): { offsets: Array<{ x: number; y: number }>; width: number; height: number } {
  if (items.length === 0) return { offsets: [], width: 0, height: 0 }

  if (layout === 'row' || items.length === 1) {
    let x = 0
    let height = 0
    const offsets = items.map(item => {
      const offset = { x, y: 0 }
      x += item.width + gapX
      height = Math.max(height, item.height)
      return offset
    })
    return { offsets, width: x - gapX, height }
  }

  const columns = Math.ceil(Math.sqrt(items.length))
  const cellWidth = Math.max(...items.map(i => i.width))
  const cellHeight = Math.max(...items.map(i => i.height))
  const rows = Math.ceil(items.length / columns)
  const offsets = items.map((_, index) => ({
    x: (index % columns) * (cellWidth + gapX),
    y: Math.floor(index / columns) * (cellHeight + gapY),
  }))
  return {
    offsets,
    width: columns * cellWidth + (columns - 1) * gapX,
    height: rows * cellHeight + (rows - 1) * gapY,
  }
}

function anchorFor(
  intent: PlacementIntent,
  cluster: { width: number; height: number },
  gapX: number,
  gapY: number,
): { x: number; y: number } {
  switch (intent.kind) {
    case 'at':
      return intent.point
    case 'flow':
      return intent.relation === 'right'
        ? { x: intent.source.x + intent.source.width + gapX, y: intent.source.y }
        : { x: intent.source.x, y: intent.source.y + intent.source.height + gapY }
    case 'near':
      return { x: intent.anchor.x - cluster.width / 2, y: intent.anchor.y - cluster.height / 2 }
    case 'frontier':
      return intent.contentBounds
        ? { x: intent.contentBounds.x, y: intent.contentBounds.y + intent.contentBounds.height + gapY }
        : { x: DEFAULT_START_X, y: DEFAULT_START_Y }
  }
}

/**
 * Resolve a placement intent into concrete positions for one or more items.
 *
 * All intents except `at` (the user aimed — never second-guess a pointer) are
 * collision-resolved: starting at the intent's anchor, the cluster slides
 * right past blockers in reading order, wraps to the next row after
 * `maxSearchSpan`, and after `maxRows` rows gives up and returns the raw
 * anchor (`clean: false`) — small local nudges, never a distant teleport.
 * Deterministic for identical inputs.
 */
export function resolvePlacement(
  intent: PlacementIntent,
  items: PlacementItem[],
  occupancy: PlacementOccupancy,
  options?: PlacementOptions,
): PlacementResult {
  const opts = { ...DEFAULTS, ...options }
  const cluster = layoutCluster(items, opts.layout, opts.gapX, opts.gapY)
  const anchor = anchorFor(intent, cluster, opts.gapX, opts.gapY)

  const finalize = (topLeft: { x: number; y: number }, clean: boolean): PlacementResult => ({
    positions: cluster.offsets.map(offset => ({ x: topLeft.x + offset.x, y: topLeft.y + offset.y })),
    cluster: { x: topLeft.x, y: topLeft.y, width: cluster.width, height: cluster.height },
    clean,
  })

  if (intent.kind === 'at') return finalize(anchor, true)

  const tolerance = opts.overlapTolerance
  const rowStep = Math.max(cluster.height, MAX_NODE_HEIGHT) + opts.gapY

  for (let row = 0; row < opts.maxRows; row++) {
    const y = anchor.y + row * rowStep
    let x = anchor.x
    while (x <= anchor.x + opts.maxSearchSpan) {
      const probe: PlacementRect = {
        x: x + tolerance,
        y: y + tolerance,
        width: Math.max(cluster.width - 2 * tolerance, 1),
        height: Math.max(cluster.height - 2 * tolerance, 1),
      }
      const blockers = occupancy.query(probe)
      if (blockers.length === 0) return finalize({ x, y }, true)
      const clearX = Math.max(...blockers.map(b => b.x + b.width)) + opts.gapX
      if (clearX <= x) break
      x = clearX
    }
  }

  return finalize(anchor, false)
}

// ============================================================================
// CHILD PLACEMENT GRAMMAR
// ============================================================================
//
// The offset-based helpers below predate the placement kernel. They express the
// canvas grammar directly — where a child lands relative to its parent encodes
// the relationship — and are still the path every generation output takes.

/** Child node vertical offset from parent. Uses MAX_NODE_HEIGHT so tall (9:16) nodes still clear. */
const CHILD_OFFSET_Y = MAX_NODE_HEIGHT + NODE_GAP_Y

/** Operations that should be placed horizontally (same row as parent) */
const HORIZONTAL_OPERATIONS = new Set(['variation', 'generate-image', 'generate-audio', 'inpaint-audio', 'offshoot'])

/** Operations that should be left-aligned when placed below parent (not shifted right) */
const LEFT_ALIGNED_OPERATIONS = new Set(['v2v'])

/**
 * Operations whose nodes render as fixed-width audio cards (AUDIO_NODE_WIDTH).
 * inpaint-audio outputs are audio nodes too, so they must use the same 300px
 * step as generate-audio — otherwise batches fall back to the 16:9 NODE_WIDTH
 * step and overlap the source clip.
 */
const AUDIO_WIDTH_OPERATIONS = new Set(['generate-audio', 'inpaint-audio'])

export type ChildPlacementDirection = 'right' | 'below-left' | 'below-right'

/**
 * The canvas placement grammar: where a child lands encodes its relationship
 * to the parent. Same row = a peer you choose between (takes, offshoots);
 * below, left-aligned = derived edit of the same content (v2v); below and
 * to the right = a continuation (extend). Every child-placement path must
 * route through this so the grammar can't fork.
 */
export function childPlacementDirection(operation?: string): ChildPlacementDirection {
  if (operation && HORIZONTAL_OPERATIONS.has(operation)) return 'right'
  if (operation && LEFT_ALIGNED_OPERATIONS.has(operation)) return 'below-left'
  return 'below-right'
}

/**
 * Compute bin-relative fallback position.
 *
 * When a node is inside a bin but has no stored position, the viewport center
 * (which is in absolute coordinates) needs to be converted to bin-relative
 * coordinates for proper placement within the bin.
 *
 * @param binId - The bin ID the node belongs to (if any)
 * @param hasStoredPosition - Whether the source/parent already has a stored position
 * @param viewportCenter - The viewport center in absolute coordinates
 * @param canvasPositions - Map of all canvas positions
 * @returns Fallback position (bin-relative if applicable, otherwise viewport center)
 */
export function computeBinRelativeFallback(
  binId: string | undefined,
  hasStoredPosition: boolean,
  viewportCenter: { x: number; y: number } | null,
  canvasPositions: Map<string, CanvasPosition>,
): { x: number; y: number } | null {
  if (!viewportCenter) return null

  // Only convert to bin-relative if node is in a bin AND has no stored position
  if (binId && !hasStoredPosition) {
    const binPosition = canvasPositions.get(`bin-${binId}`)
    if (binPosition) {
      return {
        x: viewportCenter.x - binPosition.x,
        y: viewportCenter.y - binPosition.y,
      }
    }
  }

  return viewportCenter
}

/**
 * Calculate position for a new independent node.
 * Returns the viewport center when known — overlapping there is acceptable for
 * predictability. When the viewport is unknown (initial mount, scene switch),
 * falls back to the caller-provided content frontier so nodes land near the
 * user's content instead of at an absolute point far from it.
 */
export function calculateNewShotPosition(
  viewportCenter: { x: number; y: number } | null,
  getContentFallback?: () => { x: number; y: number } | null,
): { x: number; y: number } {
  return viewportCenter ?? getContentFallback?.() ?? { x: DEFAULT_START_X, y: DEFAULT_START_Y }
}

/**
 * Calculate position for a child node (variation, extend, edit).
 *
 * Positioning strategy based on operation type:
 * - **Horizontal** (variation, generate-image, generate-audio, offshoot): Same row as parent, to the right
 * - **Edit videos (v2v)**: Directly below parent, left-aligned
 * - **Other children** (extend): Below and to the right
 *
 * @param sourcePosition - Position of the parent/source node
 * @param _existingPositions - Unused (kept for API compatibility)
 * @param fallbackPosition - Position to use if sourcePosition is undefined (e.g., viewport center)
 * @param operation - The operation type (determines horizontal vs vertical placement)
 * @param parentWidth - Width of the parent node. Defaults to NODE_WIDTH for standard 16:9, or
 *   AUDIO_NODE_WIDTH when operation is `generate-audio` (audio parents are always 300px wide).
 */
export function calculateChildPosition(
  sourcePosition: { x: number; y: number } | undefined,
  siblingIndex: number, // 1-based sibling index; 0 means no offset (legacy callers)
  _existingPositions: Map<string, CanvasPosition>,
  fallbackPosition?: { x: number; y: number } | null,
  operation?: string,
  parentWidth?: number,
): { x: number; y: number } {
  // Use source position, or fallback (viewport center) — never the raw origin,
  // which is usually far off-screen
  const basePos = sourcePosition ?? fallbackPosition ?? { x: DEFAULT_START_X, y: DEFAULT_START_Y }

  // Variations go horizontally (same row), other children go below
  const isHorizontal = operation && HORIZONTAL_OPERATIONS.has(operation)
  const isLeftAligned = operation && LEFT_ALIGNED_OPERATIONS.has(operation)

  // Audio parents are always 300px wide. Override here so callers that derive
  // parentWidth from aspect ratio (typically undefined for audio) don't end up
  // with a 200px step that overlaps the source node.
  const effectiveParentWidth =
    operation && AUDIO_WIDTH_OPERATIONS.has(operation) ? AUDIO_NODE_WIDTH : (parentWidth ?? NODE_WIDTH)
  const step = effectiveParentWidth + NODE_GAP_X
  // Use siblingIndex to offset so subsequent children don't stack on each other.
  // siblingIndex is 1-based: first child (1) gets base offset, second (2) shifts further.
  // When 0 is passed (legacy), behave like siblingIndex=1.
  const index = Math.max(siblingIndex, 1)

  // X position: left-aligned first child stays at parent X, others shift right
  const childX = isLeftAligned ? basePos.x + (index - 1) * step : basePos.x + index * step
  const childY = isHorizontal ? basePos.y : basePos.y + CHILD_OFFSET_Y

  return { x: childX, y: childY }
}

/**
 * Calculate positions for a batch of child nodes.
 *
 * Positioning strategy based on operation type:
 * - **Horizontal** (variation, generate-image, generate-audio, offshoot): Same row as parent, filling right
 * - **Edit videos (v2v)**: First child directly below (left-aligned), rest to the right
 * - **Other children** (extend): One row below and to the right
 *
 * Algorithm:
 * - Children placed with consistent NODE_GAP_X spacing (10px)
 * - Left-aligned: First child at parent.x, rest spaced horizontally
 * - Other: First child at parent.x + parentWidth + NODE_GAP_X, rest spaced horizontally
 * - Each subsequent child spaced by: childWidth + NODE_GAP_X
 * - Since variations inherit parent's aspect ratio, all have same width
 *
 * NOTE: We skip global overlap detection because canvasPositions contains
 * positions in different coordinate systems (bin-relative vs absolute) and from different
 * scenes, causing false positive overlaps.
 *
 * @param parentPosition - Position of the parent node (may be undefined)
 * @param batchSize - Number of child positions to calculate
 * @param _existingPositions - Unused (kept for API compatibility)
 * @param fallbackPosition - Position to use if parentPosition is undefined (e.g., viewport center)
 * @param operation - The operation type (determines horizontal vs vertical placement)
 * @param parentWidth - Width of the parent node. Defaults to NODE_WIDTH for standard 16:9, or
 *   AUDIO_NODE_WIDTH when operation is `generate-audio` (audio parents are always 300px wide).
 * @returns Array of positions for each child in the batch
 */
export function calculateBatchChildPositions(
  parentPosition: { x: number; y: number } | undefined,
  batchSize: number,
  _existingPositions: Map<string, CanvasPosition>,
  fallbackPosition?: { x: number; y: number } | null,
  operation?: string,
  parentWidth?: number,
): Array<{ x: number; y: number }> {
  const results: Array<{ x: number; y: number }> = []
  const basePos = parentPosition ?? fallbackPosition ?? { x: DEFAULT_START_X, y: DEFAULT_START_Y }

  // Variations go horizontally (same row), other children go below
  const isHorizontal = operation && HORIZONTAL_OPERATIONS.has(operation)
  const isLeftAligned = operation && LEFT_ALIGNED_OPERATIONS.has(operation)
  const childY = isHorizontal ? basePos.y : basePos.y + CHILD_OFFSET_Y

  // All children have the same width as parent (same aspect ratio for variations).
  // Audio parents are always 300px wide; override here so callers that derive
  // parentWidth from an aspect ratio (typically undefined for audio) don't end
  // up with a 200px step that overlaps the source node.
  const effectiveParentWidth =
    operation && AUDIO_WIDTH_OPERATIONS.has(operation) ? AUDIO_NODE_WIDTH : (parentWidth ?? NODE_WIDTH)
  // Step = childWidth + gap to maintain consistent NODE_GAP_X spacing.
  const step = effectiveParentWidth + NODE_GAP_X

  // Starting X position: left-aligned starts at parent.x, others start shifted right
  const startX = isLeftAligned ? basePos.x : basePos.x + step

  // Place children sequentially
  for (let i = 0; i < batchSize; i++) {
    const childX = startX + i * step
    results.push({ x: childX, y: childY })
  }

  return results
}

// ============================================================================
// CONTAINMENT
// ============================================================================

/**
 * Check if >50% of a node's area is inside the given bounds.
 * Uses intersection area calculation for precise containment checking.
 *
 * @param nodePos - Absolute position of the node
 * @param nodeWidth - Width of the node
 * @param nodeHeight - Height of the node
 * @param bounds - Absolute bounds to check against
 * @returns true if more than 50% of the node is inside the bounds
 */
export function checkMajorityContainment(
  nodePos: { x: number; y: number },
  nodeWidth: number,
  nodeHeight: number,
  bounds: { x: number; y: number; width: number; height: number },
): boolean {
  const nodeArea = nodeWidth * nodeHeight

  // Calculate intersection rectangle
  const intersectLeft = Math.max(nodePos.x, bounds.x)
  const intersectRight = Math.min(nodePos.x + nodeWidth, bounds.x + bounds.width)
  const intersectTop = Math.max(nodePos.y, bounds.y)
  const intersectBottom = Math.min(nodePos.y + nodeHeight, bounds.y + bounds.height)

  // No intersection
  if (intersectRight <= intersectLeft || intersectBottom <= intersectTop) {
    return false
  }

  const intersectionArea = (intersectRight - intersectLeft) * (intersectBottom - intersectTop)
  return intersectionArea > nodeArea * 0.5
}
